import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { IPage, PageStatus, PageType } from '../../../models/admin';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogModule, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ChangePageTypeDialogComponent } from './change-page-type-dialog/change-page-type-dialog.component';
import { SnackBarService } from '../../../services/snackbar.service';

@Component({
  selector: 'app-page-card',
  templateUrl: './page-card.component.html',
  styleUrls: ['./page-card.component.scss'],
  standalone: false
})
export class PageCardComponent {
  colors: string[] = [
    '#E4FF2B', // Preface
    '#9AFE7E', // Story
    '#FAD9F7', // Poem
    '#8EFDFE', // Quiz
    '#FADBD8', // About
    '#E5E8E8', // VisualSeries
    '#FFF3CD'  // Chat
  ];

  // Assuming page.Type is 1-based (1 for Preface, 2 for Story, etc.)
  getBackgroundColor(pageType: number): string {
    return this.colors[pageType] || '#FFFFFF'; // Fallback to white if pageType is invalid
  }
  @Input() page!: IPage;
  @Input() isLoading!: boolean;
  @Input() canEdit: boolean = false;
  @Input() canDelete: boolean = false;

  @Output() edit = new EventEmitter<IPage>();
  @Output() delete = new EventEmitter<IPage>();
  @Output() click = new EventEmitter<IPage>();
  @Output() pageUpdated = new EventEmitter<Partial<IPage> & {
    PageId: string;
    chapterId: string;
  }>();

  isHovered = false;
  isPageMenuOpen = false;
  pageTypes = Object.values(PageType).filter(value => typeof value === 'number');
  pageTypeMap = this.pageTypes.map((type: number) => ({ value: type, label: PageType[type] }));

  types: any = PageType;


  changePageType(): void {
    if (this.page.Status != PageStatus.Draft) {
      this.snackBarService.showSnackBar('error', "Page can only be updated in Draft state.");
      return;
    }

    const dialogRef = this.dialog.open(ChangePageTypeDialogComponent, {
      width: '420px',
      data: {
        currentPageType: this.page.Type
      }
    });

    dialogRef.afterClosed().subscribe((pageType: PageType | undefined) => {
      if (!pageType) {
        return;
      }
      // Save/update page type here
      this.updatePageData({ PageId: this.page.PageId, chapterId: this.page.chapterId, Type: pageType })
    });
  }

  onEdit(event: Event, page: IPage) {
    event.stopPropagation();
    this.edit.emit(page);
  }

  constructor(private dialog: MatDialog, private snackBarService: SnackBarService) { }

  onDelete(event: Event, page: IPage) {
    event.stopPropagation();
    event.preventDefault();
    const dialogRef = this.dialog.open(ConfirmDeletePageDialogComponent, {
      width: '300px',
      data: { pageTitle: page.Title || 'Page' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.delete.emit(page);
      }
    });
  }

  updatePageData(page: Partial<IPage> & {
    PageId: string;
    chapterId: string;
  }) {
    this.pageUpdated.emit(page);
  }

  onCardClick(event: Event, page: IPage) {
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.click.emit(page);
  }

  showPageStatusIcon(status: string) {
    return status === (this.page.Status);
  }
}

@Component({
  selector: 'app-confirm-delete-page-dialog',
  standalone: true,
  template: `
    <h2 mat-dialog-title class="warning-title">
      <mat-icon color="warn" class="warning-icon">warning</mat-icon>
      Delete Page
    </h2>

    <mat-dialog-content class="warning-content">
      Are you sure you want to delete <b>{{data.pageTitle}}</b>?
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close cdkFocusInitial>No</button>
      <button mat-button color="warn" (click)="onConfirm()">Yes</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .warning-title {
      display: flex;
      align-items: center;
      color: #d32f2f;
      font-weight: 500;
      margin: 0;
    }

    .warning-icon {
      font-size: 24px;
      line-height: 1;
      vertical-align: middle;
    }

    .warning-content {
      margin-top: 8px;
    }
  `],
  imports: [
    MatDialogModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatButtonModule,
    MatIconModule
  ]
})
export class ConfirmDeletePageDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ConfirmDeletePageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { pageTitle: string }
  ) { }

  onConfirm(): void {
    this.dialogRef.close(true);
  }
}